import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { formatCurrency, formatDate, formatNumber } from '../../../utils/formatting';
import {
    SOLD_PERIODS, countTone, excludeSubject, filterSoldComps, peerAskTone, peerSummaries, soldSummary,
} from '../../../utils/marketComps';
import { PeerPhotosBadge, PeerQuotesBadge } from '../../InventoryDashboard/UnitDetailPane/CompsPeerPopover';
import CompsSourcesV2 from './CompsSourcesV2';
import { COMPS_STEP_DURATIONS, COMPS_THINKING_STEPS } from '../../InventoryDashboard/UnitDetailPane/CompsTab';
import { tabCtxShape } from './tabContext';
import './CompsTabV2.css';

// ── Loader (P-UNIT-095): today's three timed steps ──────────────────────

function CompsLoader({ unit }) {
    const [step, setStep] = useState(0);
    useEffect(() => {
        if (step >= COMPS_THINKING_STEPS.length - 1) return undefined;
        const t = setTimeout(() => setStep((s) => s + 1), COMPS_STEP_DURATIONS[step]);
        return () => clearTimeout(t);
    }, [step]);
    const unitLabel = [unit?.YEAR_MANUF, unit?.MAKE, unit?.MODEL].filter(Boolean).join(' ');
    return (
        <div className="u2-card cmp2-loader" role="status">
            <div className="u2-c-head">
                <span className="cmp2-spin" aria-hidden="true" />
                <h3>Loading comparables</h3>
                {unitLabel && <span className="u2-sub">{unitLabel}</span>}
            </div>
            <ol className="cmp2-steps">
                {COMPS_THINKING_STEPS.map((s, i) => {
                    const state = i < step ? 'done' : i === step ? 'active' : 'pending';
                    return (
                        <li key={s.label} className={`cmp2-step cmp2-step--${state}`}>
                            <span className="cmp2-step-ic" aria-hidden="true" />
                            <span>
                                {s.label}
                                {state !== 'pending' && <small>{s.detail}</small>}
                            </span>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
CompsLoader.propTypes = { unit: PropTypes.object };

// ── Active peers and sold comps (P-UNIT-096..099) ───────────────────────

const PEER_TONE_CLASS = { low: 'u2-pos', high: 'u2-neg' };

function ActivePeers({ unit, comps }) {
    // Never the unit itself (QA P-1).
    const peers = excludeSubject(comps.active_peers, unit.StockNumber);
    // Averages of the rows shown, never including the unit (QA re-test).
    const { hours_summary: hoursSummary, listing_duration_summary: listedSummary } = peerSummaries(comps, unit.StockNumber);
    return (
        <div className="u2-card">
            <div className="u2-c-head">
                <h3>Active Peers</h3>
                <span className="u2-sub">{comps.make} {comps.model} ({comps.year}) currently in stock</span>
            </div>
            {peers.length === 0 ? (
                <p className="u2-note">No other units of this make/model/year currently in stock.</p>
            ) : (
                <>
                    {hoursSummary && (
                        <p className="u2-note">
                            {peers.length} peer{peers.length === 1 ? '' : 's'} · {formatNumber(Math.round(hoursSummary.avg))}h avg hours
                            {listedSummary && ` · ${Math.round(listedSummary.avg)}d avg listed`}
                        </p>
                    )}
                    <div className="u2-tw">
                        <table className="u2-tt" aria-label="Active peers">
                            <thead>
                                <tr>
                                    <th>Stock #</th>
                                    <th>Branch</th>
                                    <th className="r">Asking</th>
                                    <th className="r">Hours</th>
                                    <th className="r">Days</th>
                                    <th>Condition</th>
                                    <th>Photos</th>
                                    <th>Quotes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {peers.map((peer) => {
                                    const photos = peer.image_count ?? 0;
                                    const quotes = peer.quote_count ?? 0;
                                    const tone = peerAskTone(peer.asking_price, unit.Best_Sell_Price);
                                    const self = peer.stock_number === unit.StockNumber;
                                    return (
                                        <tr key={peer.stock_number} className={self ? 'is-me' : undefined}>
                                            <td>#{peer.stock_number}</td>
                                            <td>{peer.branch || '—'}</td>
                                            <td className={['r', PEER_TONE_CLASS[tone]].filter(Boolean).join(' ')}>
                                                {formatCurrency(peer.asking_price)}
                                            </td>
                                            <td className="r">{peer.hours != null ? formatNumber(peer.hours) : '—'}</td>
                                            <td className="r">{peer.days_in_stock != null ? `${peer.days_in_stock}d` : '—'}</td>
                                            <td>{peer.new_used || '—'}</td>
                                            <td>
                                                <PeerPhotosBadge
                                                    stockNumber={String(peer.stock_number)}
                                                    count={photos}
                                                    badgeClass={`comps-photos-${countTone(photos, 5)}`}
                                                />
                                            </td>
                                            <td>
                                                <PeerQuotesBadge
                                                    stockNumber={String(peer.stock_number)}
                                                    count={quotes}
                                                    badgeClass={`comps-photos-${countTone(quotes, 3)}`}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
ActivePeers.propTypes = { unit: PropTypes.object.isRequired, comps: PropTypes.object.isRequired };

function SoldComps({ comps }) {
    const [years, setYears] = useState(1);
    const rows = filterSoldComps(comps.sold_comps, years);
    const stats = rows.length > 0 ? soldSummary(rows) : null;
    return (
        <div className="u2-card">
            <div className="u2-c-head">
                <h3>Sold Comps</h3>
                <span className="u2-sub">previously sold {comps.make} {comps.model} ({comps.year})</span>
                <span className="u2-right">
                    <select className="cmp2-in cmp2-sel" aria-label="Sold period" value={years} onChange={(e) => setYears(Number(e.target.value))}>
                        {SOLD_PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                </span>
            </div>
            {stats && (
                <p className="u2-note">
                    {stats.count} sold · {stats.avg}d avg · {stats.min}d fastest · {stats.max}d slowest
                </p>
            )}
            {rows.length === 0 ? (
                <p className="u2-note">No sold units found for the selected period.</p>
            ) : (
                <div className="u2-tw">
                    <table className="u2-tt" aria-label="Sold comps">
                        <thead>
                            <tr>
                                <th>Stock #</th>
                                <th className="r">Sale price</th>
                                <th className="r">Hours</th>
                                <th className="r">Days to sell</th>
                                <th>Sold date</th>
                                <th>Branch</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((s) => (
                                <tr key={s.stock_number}>
                                    <td>#{s.stock_number}</td>
                                    <td className="r">{formatCurrency(s.sale_price)}</td>
                                    <td className="r">{s.hours != null ? formatNumber(s.hours) : '—'}</td>
                                    <td className="r">{s.days_to_sell != null ? `${s.days_to_sell}d` : '—'}</td>
                                    <td>{s.sold_date ? formatDate(s.sold_date) : '—'}</td>
                                    <td>{s.branch || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
SoldComps.propTypes = { comps: PropTypes.object.isRequired };

/**
 * Comps (P-UNIT-095..099) on the full unit page: the market sources view
 * (dealer sold, auction, advertised and your sales over one filter state,
 * CompsSourcesV2; it replaced the advertised-only "Market position" band and
 * Evidence Explorer, docs/decisions/comps-sources.md "Frontend"), then active
 * peers and sold comps side by side. The sources load on their own, keyed by
 * stock number; only the peers / sold part waits on the "Loading
 * comparables" loader.
 */
export default function CompsTabV2({ ctx }) {
    const { unit, detail, machineCatalog } = ctx;
    const stock = unit.StockNumber;
    const { comps, compsLoading } = detail;

    return (
        <div className="cmp2">
            {Boolean(stock) && <CompsSourcesV2 key={stock} unit={unit} machineCatalog={machineCatalog} />}
            {compsLoading && <CompsLoader unit={unit} />}
            {!compsLoading && comps && (
                <div className="u2-grid2 u2-grid2--even">
                    <ActivePeers unit={unit} comps={comps} />
                    <SoldComps comps={comps} />
                </div>
            )}
        </div>
    );
}

CompsTabV2.propTypes = { ctx: tabCtxShape.isRequired };
